import { classnames } from '@aibees/shared/utils';
import { MenuItem, SelectProps } from '@mui/material';
import { memo, useCallback, useMemo, useState } from 'react';
import { DropDownOption } from './components/drop-down-option';
import { DropDownValue } from './components/drop-down-value';
import * as Styled from './styles';
import type {
  DropDownProps as Props,
  DropDownRenderItem as RenderItem,
} from './types';

const defaultRender: RenderItem = (
  label,
  option,
  multiple,
  withCheckbox,
  withRadio,
  withCheckmark,
  selected,
  size
) => (
  <DropDownOption
    label={label}
    option={option}
    multiple={multiple ?? false}
    withCheckbox={withCheckbox}
    withRadio={withRadio}
    withCheckmark={withCheckmark}
    selected={selected}
    size={size}
  />
);

export const DropDown = memo(
  ({
    className,
    options,
    value,
    showEmptyOption,
    emptyText,
    placeholder,
    disabledItems,
    onChange,
    multiple,
    size,
    variant,
    children,
    renderItem,
    renderValue,
    withRadio,
    withCheckbox,
    withCheckmark,
    ...props
  }: Props) => {
    const render: RenderItem = renderItem ?? defaultRender;

    const v = useMemo(() => {
      if (value === '') {
        return multiple ? [] : '';
      }
      if (multiple) {
        // If already an array, return as-is; if single value, wrap in array
        return Array.isArray(value) ? value : [value];
      }
      // For single selection, if array is passed, take first item
      return Array.isArray(value) ? value[0] : value;
    }, [value, multiple]);

    const DROP_DOWN_MENU_PROPS = useMemo(
      () => ({
        anchorOrigin: {
          vertical: 'bottom' as const,
          horizontal: 'left' as const,
        },
        transformOrigin: {
          vertical: 'top' as const,
          horizontal: 'left' as const,
        },
        ...props.MenuProps,
      }),
      [props.MenuProps]
    );

    const [innerState, setInnerState] = useState(v);
    const handleChange = onChange ?? setInnerState;
    const currentState = onChange ? v : innerState;

    const renderValueFn = useCallback(() => {
      return (
        <DropDownValue
          multiple={multiple}
          options={options}
          placeholder={placeholder}
          value={currentState}
          renderValue={renderValue}
        />
      );
    }, [currentState, multiple, options, placeholder, renderValue]);

    const onChangeFn: SelectProps['onChange'] = (e) => {
      handleChange?.(e.target.value);
    };

    return (
      <Styled.Wrapper
        className={classnames('DropDown', className)}
        value={currentState}
        displayEmpty
        multiple={multiple}
        variant={variant}
        size={size}
        fullWidth
        autoWidth={false}
        {...props}
        renderValue={renderValueFn}
        onChange={onChangeFn}
        MenuProps={DROP_DOWN_MENU_PROPS}
      >
        {showEmptyOption && (
          <MenuItem value="">{emptyText ?? placeholder}</MenuItem>
        )}

        {!children
          ? options?.map((x) => (
              <MenuItem
                className="DropDownOption"
                key={x.key}
                value={(x.value ?? x.key) as string}
                disabled={disabledItems?.includes(x.key)}
              >
                {render(
                  x.label ?? x.value ?? x.key,
                  x,
                  multiple ?? false,
                  withCheckbox,
                  withRadio,
                  withCheckmark,
                  currentState,
                  size
                )}
              </MenuItem>
            ))
          : children}
      </Styled.Wrapper>
    );
  }
);

DropDown.displayName = 'DropDown';
